import { Injectable, inject } from '@angular/core';
import { HubConnection, HubConnectionState } from '@microsoft/signalr';
import { Subject } from 'rxjs';

import { HubConnectionFactory } from './hub-connection.factory';
import { SignalRHubNames } from './signalr-hub-names';

export type AgentPresenceStatus = 'Online' | 'Busy' | 'Away' | 'Offline';

export interface AgentPresenceChangedEvent {
  userId: string;
  status: AgentPresenceStatus;
  occurredAtUtc: string;
}

/** Communication Platform v2's Agent Presence subsystem -- one connection per browser tab
 * (Multi-tab Support is handled server-side by PresenceConnectionTracker, not here). Sends
 * a heartbeat every 30 seconds while connected; comfortably inside the backend's own
 * 2-minute PresenceTimeoutOptions.HeartbeatTimeoutMinutes window so a single missed beat
 * from a slow network never falsely flips this agent Offline. */
@Injectable({ providedIn: 'root' })
export class PresenceHubService {
  private static readonly HEARTBEAT_INTERVAL_MS = 30_000;

  private readonly connection: HubConnection = inject(HubConnectionFactory).create(SignalRHubNames.presence);
  private readonly agentPresenceChangedSubject = new Subject<AgentPresenceChangedEvent>();
  private heartbeatTimer?: ReturnType<typeof setInterval>;

  readonly agentPresenceChanged$ = this.agentPresenceChangedSubject.asObservable();

  constructor() {
    this.connection.on('AgentPresenceChanged', (payload: AgentPresenceChangedEvent) => this.agentPresenceChangedSubject.next(payload));
  }

  async start(): Promise<void> {
    if (this.connection.state === HubConnectionState.Disconnected) {
      await this.connection.start();
      this.scheduleHeartbeat();
    }
  }

  async stop(): Promise<void> {
    clearInterval(this.heartbeatTimer);
    await this.connection.stop();
  }

  async setPresence(status: 'Online' | 'Busy' | 'Away'): Promise<void> {
    if (this.connection.state === HubConnectionState.Connected) {
      await this.connection.invoke('SetPresence', status);
    }
  }

  private scheduleHeartbeat(): void {
    clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = setInterval(() => {
      if (this.connection.state === HubConnectionState.Connected) {
        void this.connection.invoke('Heartbeat');
      }
    }, PresenceHubService.HEARTBEAT_INTERVAL_MS);
  }
}
